import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compact, first, map, uniqueId, isEmpty } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SmartActionButton, getDirectionUri } from '@bingads-webui-campaign-react/smart-component-action-button';
import {
  useClientContext,
  useIsBusinessHourSectionEnabled,
  useIsSmartPageMapEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import { ResponsiveMode, sendMtagData } from '@bingads-webui-campaign-react/smart-component-util';
import {
  BingMapsContext,
  BingMap,
} from '@bingads-webui-campaign-react/smart-component-bing-maps';
import { SmartBusinessHoursConnector } from '@bingads-webui-campaign-react/smart-component-business-hours';
import { TooltipWrapper } from '@bingads-webui-campaign-react/smart-component-primitive';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';

import { dataProperties } from './data-config';
import { validateGeoLocation } from './components/validations';
import { toggleValueToActionButtonTypeMapping } from './components/component-config-reader';


const getStyles = (components, { palette = {} }) => ({
  container: {
    ...components.section,
    marginLeft: '13%',
    marginRight: '13%',
    marginBottom: 32,
  },
  containerWithOutBizHour: {
    ...components.section,
    marginLeft: '13%',
    marginRight: '13%',
  },
  title: {
    ...components.sectionTitle,
  },
  mapContainer: {
    height: 260,
    width: '100%',
    marginBottom: 28,
    '& .MicrosoftMap': {
      zIndex: 0,
    },
  },
  hidden: {
    display: 'none',
  },
  mapItem: {
    height: '100%',
    width: '100%',
  },
  contactMethodsItemContainer: {
    paddingBottom: 28,
    display: 'flex',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'center',
  },
  contactMethodsItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'space-between',
    textAlign: 'center',
    minWidth: 301,
    maxWidth: 301,
    height: 210,
    boxShadow: '0px 1.6px 3.6px rgba(0, 0, 0, 0.132), 0px 0.3px 0.9px rgba(0, 0, 0, 0.108)',
    borderRadius: 2,
    margin: '0px 26px',
    background: palette.cardBackground,
  },
  contactMethodsItemTitle: {
    color: palette.secondaryText,
    fontSize: 24,
    lineHeight: '20px',
    marginTop: 27,
    fontWeight: 600,
  },
  contactMethodsItemContent: {
    color: palette.secondaryText,
    fontSize: 16,
    lineHeight: '20px',
    marginTop: 12,
    marginBottom: 12,
    marginLeft: '5%',
    marginRight: '5%',
  },
  contactMethodsActionButton: {
    marginTop: 'auto',
    marginBottom: 20,
  },
  contactMethodsItemButton: {
    ...components.defaultButton,
    border: `2px solid ${palette.secondaryText}`,
    fontWeight: 'bold',
    fontSize: 18,
    lineHeight: '24px',
    padding: '6px 20px 10px 20px',
  },
  businessHoursContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  mobileMode: {
    '& $contactMethodsItemContainer': {
      display: 'block',
      paddingBottom: 0,
      marginLeft: 0,
    },
    '& $contactMethodsItem': {
      marginBottom: 28,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  smallDesktopMode: {
    '& $contactMethodsItemContainer': {
      display: 'block',
      paddingBottom: 0,
      marginLeft: 0,
    },
    '& $contactMethodsItem': {
      minWidth: '400px !important',
      maxWidth: '400px !important',
      marginBottom: 28,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
});

export const UnStyledSmartContactView = ({
  classes,
  i18n,
  id,
  showEmail,
  emailList,
  phoneNumber,
  showPhone,
  showLocation,
  locationInfo,
  geoLocationInfo,
  showHours,
  showMap,
  isEditMode,
}) => {
  const isBusinessHourSectionEnabled = useIsBusinessHourSectionEnabled();
  const isSmartPageMapEnabled = useIsSmartPageMapEnabled();
  const { isRuntime, responsiveMode } = useClientContext();
  const { Maps, loadBingMapsSdk, bingMapsApiKey } = useContext(BingMapsContext);

  const [bingMap, setBingMap] = useState(null);

  // Editing mode: Show map without caring the warnings
  // Non editing mode: If there is any warnings, hide the map
  const hideInNonEditingMode = !isEditMode && !validateGeoLocation(geoLocationInfo);
  const isShowingBingMap = isSmartPageMapEnabled && showMap && !hideInNonEditingMode;

  const contactMethods = compact([
    showPhone !== false &&
    {
      title: i18n.getString(_TL_('Call Us')),
      content: phoneNumber,
      property: dataProperties.phoneNumber,
      placeholder: `<${i18n.getString(_TL_('Phone number'))}>`,
      renderFooter: () => (
        <SmartActionButton
          id={uniqueId('contact_phone_cta_')}
          isEditMode={isEditMode}
          isRuntime={isRuntime}
          overrideButtonConfig={{
            actionType: toggleValueToActionButtonTypeMapping.showPhone,
            'data-source-name': dataProperties.phoneNumber,
          }}
          className={classes.contactMethodsItemButton}
        />
      ),
    },
    showEmail !== false &&
    {
      title: i18n.getString(_TL_('Email Us')),
      content: first(emailList),
      property: dataProperties.emails,
      placeholder: `<${i18n.getString(_TL_('Email'))}>`,
      renderFooter: () => (
        <SmartActionButton
          id={uniqueId('contact_email_cta_')}
          isEditMode={isEditMode}
          isRuntime={isRuntime}
          overrideButtonConfig={{
            actionType: toggleValueToActionButtonTypeMapping.showEmail,
            'data-source-name': dataProperties.emails,
          }}
          className={classes.contactMethodsItemButton}
        />
      ),
    },
    showLocation !== false &&
    {
      title: i18n.getString(_TL_('Visit Us')),
      content: locationInfo,
      property: dataProperties.locationInfo,
      placeholder: `<${i18n.getString(_TL_('Address'))}>`,
      renderFooter: () => (
        <SmartActionButton
          id={uniqueId('contact_address_cta_')}
          isEditMode={isEditMode}
          isRuntime={isRuntime}
          overrideButtonConfig={{
            actionType: toggleValueToActionButtonTypeMapping.showLocation,
            'data-source-name': dataProperties.locationInfo,
          }}
          className={classes.contactMethodsItemButton}
        />
      ),
    },
  ]);

  const contactRefs = useRef([]);
  contactRefs.current = map(contactMethods, (value, index) => {
    contactRefs.current[index] = undefined;
  });

  let responsiveModeClass;
  if (responsiveMode === ResponsiveMode.Mobile) {
    responsiveModeClass = 'mobileMode';
  } else if (responsiveMode === ResponsiveMode.SmallDesktop) {
    responsiveModeClass = 'smallDesktopMode';
  } else {
    // medium and large behave the same
    responsiveModeClass = 'largeDesktopMode';
  }

  const trackCopyEvent = useCallback(() => {
    if (isRuntime) {
      sendMtagData();
    }
  }, [isRuntime]);

  const bingMapPushpinEventHandler = useCallback(() => {
    if (!isEditMode && !isEmpty(locationInfo)) {
      const directionUri = getDirectionUri(locationInfo);
      if (directionUri) {
        window.open(directionUri);
      }
    }
  }, [isEditMode, locationInfo]);

  // make sure SDK is loaded
  useEffect(() => {
    if (!bingMap && isShowingBingMap) {
      loadBingMapsSdk();
    }
  }, [loadBingMapsSdk, isShowingBingMap, bingMap]);

  useEffect(() => {
    // Add pushpin into the map
    if (bingMap && !isEmpty(geoLocationInfo) && isShowingBingMap) {
      bingMap.addPushpinToCenter(
        geoLocationInfo,
        {
          eventType: 'click',
          handler: bingMapPushpinEventHandler,
        }
      );
    }
  }, [bingMap, bingMapPushpinEventHandler, geoLocationInfo, isShowingBingMap]);

  useEffect(() => {
    // If Maps API has been loaded but map object not created yet
    // Only create once, use CSS to hide map to improve performance
    if (Maps !== null && bingMap === null && isShowingBingMap) {
      const newBingMap = new BingMap({
        bingMapsApiKey,
        element: '#smart-page-bing-map',
        Maps,
      });

      setBingMap(newBingMap);
    }
  }, [Maps, bingMap, bingMapsApiKey, setBingMap, isShowingBingMap]);

  // useEffect(() => {
  //   if (bingMap) {
  //     bingMap.map.getCredentials((key) => {});
  //   }
  // }, [bingMap]);

  useEffect(() => {
    map(contactMethods, (value, index) => {
      if (contactRefs.current[index]) {
        const currentElement = contactRefs.current[index];
        currentElement.addEventListener('copy', trackCopyEvent);
      }
    });

    return () => {
      map(contactMethods, (value, index) => {
        if (contactRefs.current[index]) {
          const currentElement = contactRefs.current[index];
          currentElement.removeEventListener('copy', trackCopyEvent);
        }
      });
    };
  }, [contactMethods, trackCopyEvent]);

  const containerClass = isBusinessHourSectionEnabled
    ? classes.containerWithOutBizHour
    : classes.container;
  const bingMapClass = classnames(
    classes.mapContainer,
    isShowingBingMap ? '' : `${classes.hidden} hidden`
  );

  return (
    <div
      id={id}
      className={classnames(classes[responsiveModeClass], containerClass, 'contact-view')}
    >
      <h1 className={classes.title}>
        {i18n.getString(_TL_('Contact Us'))}
      </h1>
      <div className={classes.contactMethodsItemContainer} >
        {
          map(contactMethods, ({
            title,
            content,
            property,
            placeholder,
            renderFooter,
          }, index) => (
            <div className={classes.contactMethodsItem} key={index} data-property={property}>
              <div className={classes.contactMethodsItemTitle}>{title}</div>
              <TooltipWrapper
                content={content || placeholder}
                lineClamp={4}
              >
                <div
                  className={classes.contactMethodsItemContent}
                  id={property}
                  // eslint-disable-next-line no-return-assign
                  ref={el => (contactRefs.current[index] = el)}
                >
                  {content || placeholder}
                </div>
              </TooltipWrapper>
              <div className={classes.contactMethodsActionButton}>{renderFooter()}</div>
            </div>
          ))
        }
      </div>
      <div className={bingMapClass}>
        <div id="smart-page-bing-map" className={classes.mapItem} />
      </div>
      {
        (!isBusinessHourSectionEnabled && showHours) ? (
          <div className={classes.businessHoursContainer}>
            <SmartBusinessHoursConnector id={id} />
          </div>
        ) : null
      }
    </div>
  );
};

UnStyledSmartContactView.propTypes = {
  classes: PropTypes.shape({
    title: PropTypes.string.isRequired,
    mobileMode: PropTypes.string.isRequired,
    smallDesktopMode: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  emailList: PropTypes.arrayOf(PropTypes.string),
  phoneNumber: PropTypes.string,
  locationInfo: PropTypes.string,
  geoLocationInfo: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    isFallbackLoc: PropTypes.bool,
  }),
  showPhone: PropTypes.bool,
  showEmail: PropTypes.bool,
  showLocation: PropTypes.bool,
  showHours: PropTypes.bool,
  isEditMode: PropTypes.bool,
  showMap: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
};

UnStyledSmartContactView.defaultProps = {
  emailList: [],
  phoneNumber: '',
  locationInfo: '',
  geoLocationInfo: null,
  showPhone: true,
  showEmail: true,
  showLocation: true,
  showHours: true,
  isEditMode: false,
  showMap: undefined,
};

export const SmartContactView = withErrorView(withDefaultStyles(
  UnStyledSmartContactView,
  getStyles
), 'componentMode');
